import { useEffect, useState } from "react";

/**
 * Layered client-side deterrence for room content.
 *
 * This deliberately does not claim to make visible content impossible to
 * capture. It blocks casual browser copying/saving and adds an attributable
 * watermark, while the backend remains responsible for authentication and
 * rate controls. Editable fields remain usable for chat, names, and forms.
 */
function isEditable(target) {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest("input, textarea, select, [contenteditable='true'], [data-room-allow-copy='true']"));
}

export default function RoomContentGuard({ enabled, username }) {
  const [stamp, setStamp] = useState("");

  useEffect(() => {
    if (!enabled) return undefined;
    const updateStamp = () => setStamp(new Date().toLocaleString([], { hour: "2-digit", minute: "2-digit" }));
    updateStamp();
    const timer = window.setInterval(updateStamp, 60_000);

    const onContextMenu = (event) => {
      if (!isEditable(event.target)) event.preventDefault();
    };
    const onDragStart = (event) => {
      if (!isEditable(event.target)) event.preventDefault();
    };
    const onCopy = (event) => {
      if (!isEditable(event.target)) event.preventDefault();
    };
    const onKeyDown = (event) => {
      const key = String(event.key || "").toLowerCase();
      const mod = event.ctrlKey || event.metaKey;
      const inspectionShortcut = event.key === "F12" || (mod && event.shiftKey && ["i", "j", "c"].includes(key));
      const saveOrSourceShortcut = mod && ["s", "u"].includes(key);
      if (!isEditable(event.target) && (inspectionShortcut || saveOrSourceShortcut)) event.preventDefault();
    };

    document.addEventListener("contextmenu", onContextMenu, true);
    document.addEventListener("dragstart", onDragStart, true);
    document.addEventListener("copy", onCopy, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("contextmenu", onContextMenu, true);
      document.removeEventListener("dragstart", onDragStart, true);
      document.removeEventListener("copy", onCopy, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [enabled]);

  if (!enabled) return null;
  return (
    <div
      className="pointer-events-none fixed inset-0 z-[45] overflow-hidden select-none"
      aria-hidden="true"
      data-testid="room-content-guard"
    >
      <div className="absolute -right-7 top-1/2 -rotate-45 text-[10px] sm:text-xs uppercase tracking-[0.24em] text-white/[0.13] whitespace-nowrap">
        Private room · {username || "Participant"} · {stamp}
      </div>
    </div>
  );
}
